import React from "react";
import styled from "styled-components";
import { useMappedState } from "redux-react-hook";
import * as yup from "yup";
import { Formik } from "formik";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { IPriceList } from "src/core/shared/api/price-change.api";
import { IStore } from "src/core/shared/store";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { ERPSeedless } from "src/utils/constants";
import {
  usePricesApi,
  PricesRequestName
} from "src/core/prices/api/prices.api";

export interface CreateUpdatePriceListModalProps {
  modalData?: Pick<IPriceList, "id" | "name" | "code"> | null;
  reloadTable?: () => void;
}

const createPriceListForm: (data: Pick<IPriceList, "name" | "code"> | null) => IFormField[] = data => {
  return [
    {
      type: "input",
      name: "name",
      value: data?.name,
      label: "Price List Name",
      placeholder: "Name",
      validation: yup
        .string()
        .min(1)
        .required("Price List Name is required")
    },
    {
      type: "input",
      name: "code",
      value: data?.code,
      label: "Code",
      placeholder: "Code",
      validation: yup
        .string()
        .min(1)
        .required("Price List Code is required")
    }
  ];
};

export const CreateUpdatePriceListModal: React.ReactFCWithChildren<Pick<BasicModalProps, "onDismiss" | "open"> & CreateUpdatePriceListModalProps> = ({
  onDismiss, open, reloadTable, modalData
}) => {
  const [ createPriceListResponse, createPriceList ] = usePricesApi(PricesRequestName.CREATE_PRICE_LIST);
  const [ updatePriceListResponse, updatePriceList ] = usePricesApi(PricesRequestName.UPDATE_PRICE_LIST);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const formRef = React.useRef<Formik>(null);

  /**
   * intended use for successful create/update requests,
   * resets the form, reloads table and actions onDismiss logic from parent.
   */
  const resetComponentState = React.useCallback(() => {
    if (formRef.current) {
      formRef.current.resetForm();
    }

    reloadTable && reloadTable();
    onDismiss();
  }, [ onDismiss, reloadTable ]);

  React.useEffect(() => {
    if (createPriceListResponse.data) {
      toast.success("Your price list has been successfully created.");
      createPriceListResponse.data = null;
      resetComponentState();
    }

    if (createPriceListResponse.error) {
      toast.error(`Your price list could not be created - ${createPriceListResponse.error.message}`);
      createPriceListResponse.error = null;
    }
  }, [ createPriceListResponse, resetComponentState ]);

  React.useEffect(() => {
    if (updatePriceListResponse.data) {
      toast.success("Your price list has been successfully updated.");
      updatePriceListResponse.data = null;
      resetComponentState();
    }

    if (updatePriceListResponse.error) {
      toast.error(`Your price list could not be updated - ${updatePriceListResponse.error.message}`);
      updatePriceListResponse.error = null;
    }
  }, [ resetComponentState, updatePriceListResponse ]);

  return (
    <StyledBasicModal
      title={`${modalData ? "Edit" : "Create New"} Price List`}
      open={open}
      onDismiss={onDismiss}
    >
      {erp === ERPSeedless && (
        <NewPriceListWrapper>
          <FormWrapper>
            <Form
              ref={formRef}
              error={(modalData ? updatePriceListResponse.error?.message : createPriceListResponse.error?.message) || null}
              submitting={createPriceListResponse.pending}
              fields={createPriceListForm(modalData || null)}
              onSubmit={({ name, code }) => {
                modalData ?
                  updatePriceList({
                    id: modalData.id,
                    name,
                    code
                  }) :
                  createPriceList({
                    name,
                    code
                  });
              }}
              submitButtonText={`${modalData ? "Update" : "Create"} Price List`}
            />
          </FormWrapper>
        </NewPriceListWrapper>
      )}
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  .submit-button {
    margin-top: ${({ theme }) => theme.spacing.margins.md};
  }
`;

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  form {
    min-width: 350px;
  }
`;

const NewPriceListWrapper = styled(Grid.Box)`
  padding: 0 15px;
`;

export default CreateUpdatePriceListModal;
